import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, Chip, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import AddIcon from "@mui/icons-material/Add";
import AddTeacher from "../../popup/AddTeacher";
import TeacherDetails from "../../popup/TeacherDetails";
import TeachersFilter from "../../popup/TeachersFilter";
import { useConstants } from "../../hooks/UseConstants";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import { useTeachersFilter } from "../../filter/UseTeachersFilter";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearch } from "../../hooks/UseSearch";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import { usePagination } from "../../hooks/UsePagination";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import AdminListToolbar from "../../components/AdminListToolbar";
import ModalOverlay from "../../components/ModalOverlay";

function Teachers() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch, order, setOrder } = useSearch();
    const { getWait, setGetWait, filterWait, setFilterWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const { majorId, setMajorId, majorsValue, setMajorsValue, teacherSpecializations, setTeacherSpecializations, teacherSpecializationsValue, setTeacherSpecializationsValue, academicDegree, setAcademicDegree, academicDegreeValue, setAcademicDegreeValue } = useTeachersFilter();
    const [teachersCounts, setTeachersCounts] = useState(0);
    const [teachers, setTeachers] = useState([]);
    const [teacher, setTeacher] = useState(null);

    const teachersUrl = useMemo(() => (
        `${host}/admin/users?account_role=teacher&page=${page + 1}&search=${search}&direction=asc&${order ? `order_by=${order}` : ""}${majorId ? `&major_id=${majorId}` : ""}${teacherSpecializations ? `&specialization_id=${teacherSpecializations}` : ""}${academicDegree ? `&academic_degree_id=${academicDegree}` : ""}`
    ), [host, page, search, order, majorId, teacherSpecializations, academicDegree]);

    const getTeachers = async () => {
        const result = await Fetch(teachersUrl, "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setTeachersCounts(pagination.total);
            setTeachers(items);
            setCurrentPage(page);
        } else {
            setTeachers([]);
            setTeachersCounts(0);
        }

        setGetWait(false);
    };

    const filteringTeachers = async () => {
        const result = await Fetch(teachersUrl, "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setTeachersCounts(pagination.total);
            setTeachers(items);
            setCurrentPage(page);
            setPopup("filter", "none");
        }

        setFilterWait(false);
    };

    const teacherDetails = (id) => {
        setTeacher(teachers.find((item) => item.id === id) || null);
        setPopup("details", "flex");
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getTeachers();
        }
    }, [wait, page, search, order]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <AdminPageHero
                    actions={
                        <Button
                            variant="contained"
                            onClick={() => setPopup("add", "flex")}
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.35)" } }}
                        >
                            <AddIcon fontSize="small" />
                            <FormattedMessage id="add_teacher" />
                        </Button>
                    }
                />

                <DashboardPanelCard
                    title={isAr ? "إدارة المدرسين" : "Teachers management"}
                    subtitle={isAr ? "تابع حسابات المدرسين وحالتهم الأكاديمية وحالة التفعيل من مكان واحد." : "Track teacher accounts, academic status, and activation state from one place."}
                    className="mx-0 !mt-0"
                >
                    <AdminListToolbar
                        isAr={isAr}
                        searchPlaceholder={intl.formatMessage({ id: "search_teachers" })}
                        search={search}
                        onSearchChange={setSearch}
                        order={order}
                        onOrderChange={setOrder}
                        totalLabel={intl.formatMessage({ id: "total_teachers" })}
                        totalCount={teachersCounts}
                        onFilterClick={() => setPopup("filter", "flex")}
                        orderOptions={[
                            { value: "", label: intl.formatMessage({ id: "date" }) },
                            { value: "first_name", label: intl.formatMessage({ id: "teacher_name" }) },
                            { value: "email", label: intl.formatMessage({ id: "email" }) },
                        ]}
                    />

                    <TableContainer component={Paper} dir={language === "en" ? "ltr" : "rtl"} sx={{ boxShadow: "none", borderRadius: 3 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="teachers table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="specialization" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="academic_degree" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="email" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}>{isAr ? "الحالة" : "Status"}</StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center"><FormattedMessage id="phone" /></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {teachers.length > 0 ? teachers.map((item) => (
                                    <StyledTableRow
                                        key={item.id}
                                        onClick={() => teacherDetails(item.id)}
                                        sx={{
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: theme.palette.mode === "dark" ? "action.hover" : "#f8fafc",
                                            },
                                        }}
                                    >
                                        <StyledTableCell align={language === "en" ? "left" : "right"} component="th" scope="row">
                                            {`${item.first_name || ""} ${item.last_name || ""}`.trim()}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            {language === "en" ? item.specialization?.name_en : item.specialization?.name_ar}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            {language === "en" ? item.academic_degree?.name_en : item.academic_degree?.name_ar}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            {item.email}
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>
                                            <Chip
                                                size="small"
                                                label={item.is_active ? (isAr ? "مفعّل" : "Active") : (isAr ? "بانتظار التفعيل" : "Pending")}
                                                color={item.is_active ? "success" : "warning"}
                                                variant="outlined"
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center" dir="ltr">
                                            {`${item.phone_code || ""}${item.phone || ""}`}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                {isAr ? "لا يوجد مدرسون مطابقون للبحث أو التصفية الحالية." : "No teachers match the current search or filters."}
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }} dir="rtl">
                        <Button disabled={page + 1 === totalPages || totalPages === 0} onClick={() => setPage(currentPage + 1)}>
                            <NavigateNextIcon fontSize="large" />
                        </Button>
                        <Typography variant="body1" className="!text-xl" dir="ltr">
                            {totalPages ? currentPage + 1 : 0} / {totalPages || 0}
                        </Typography>
                        <Button disabled={page + 1 === 1} onClick={() => setPage(currentPage - 1)}>
                            <NavigateBeforeIcon fontSize="large" />
                        </Button>
                    </Box>
                </DashboardPanelCard>
            </PageContent>

            <ModalOverlay id="filter" onClose={() => setPopup("filter", "none")}>
                <TeachersFilter
                    onClickClose={() => setPopup("filter", "none")}
                    onClickConfirm={filteringTeachers}
                    setAcademicDegree={setAcademicDegree}
                    filterWait={filterWait}
                    setFilterWait={setFilterWait}
                    setMajorId={setMajorId}
                    setTeacherSpecializations={setTeacherSpecializations}
                    academicDegreeValue={academicDegreeValue}
                    setAcademicDegreeValue={setAcademicDegreeValue}
                    majorsValue={majorsValue}
                    setMajorsValue={setMajorsValue}
                    teacherSpecializationsValue={teacherSpecializationsValue}
                    setTeacherSpecializationsValue={setTeacherSpecializationsValue}
                />
            </ModalOverlay>

            <ModalOverlay id="add" onClose={() => setPopup("add", "none")}>
                <AddTeacher setTeachers={setTeachers} onClickClose={() => setPopup("add", "none")} setSnackBar={setSnackBar} />
            </ModalOverlay>

            <ModalOverlay id="details" onClose={() => setPopup("details", "none")}>
                {teacher ? (
                    <TeacherDetails
                        teacher={teacher}
                        onClickClose={() => setPopup("details", "none")}
                        setSnackBar={setSnackBar}
                        onUpdated={(updated) => {
                            setTeacher(updated);
                            setTeachers((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
                        }}
                    />
                ) : null}
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Teachers;